'use client'

import { C, Callout, Exercise, H, Lesson, M, MB, OL, P, Step, UL } from '../_components/lesson'
import * as EX from './exercises/prob-extra'

export default function ProjectNaiveBayes() {
  return (
    <Lesson>
      <Step>
        <H>Project: a spam filter</H>
        <P>
          Early email spam filters used exactly the maths from this module, and they worked very well. You’ll build one from
          scratch and test it on emails it has never seen.
        </P>
        <OL>
          <li>Train: count words in spam and in normal (“ham”) email.</li>
          <li>Classify: combine the evidence from every word with Bayes’ rule, in log space.</li>
          <li>Evaluate: measure accuracy on held-out emails, and see which words give spam away.</li>
        </OL>
      </Step>

      <Step gate="nb-train">
        <H>Part 1 · Count</H>
        <P>
          The model is just a set of counts: how often each word appears across all spam, and across all ham. That’s maximum
          likelihood for word probabilities, the “count and divide” from two lessons ago.
        </P>
        <Exercise
          id="nb-train"
          title="train"
          {...EX.nbTrain}
          hints={[
            <>
              For each email: pick <C>{'const counts = e.spam ? model.spamCounts : model.hamCounts'}</C>, then for each word{' '}
              <C>{'counts.set(w, (counts.get(w) ?? 0) + 1)'}</C>.
            </>,
            <>Also add each word to the vocab, bump spamWords or hamWords, and count spam emails to set pSpam at the end.</>,
          ]}
        />
      </Step>

      <Step gate="nb-classify">
        <H>Part 2 · Combine the evidence</H>
        <P>
          For a new email, Bayes’ rule compares the two explanations. The <em>naive</em> part is pretending each word is
          independent of the others given the class, so their probabilities simply multiply. In logs they add, which avoids
          underflow:
        </P>
        <MB>{'\\text{score} = \\log\\frac{P(\\text{spam})}{P(\\text{ham})} + \\sum_{w \\in \\text{email}} \\log\\frac{P(w \\mid \\text{spam})}{P(w \\mid \\text{ham})}'}</MB>
        <P>
          Positive score: spam. One problem: a word never seen in ham gets probability 0, and <M>{'\\log 0 = -\\infty'}</M>{' '}
          wipes out all the other evidence. The fix is <strong>add-one smoothing</strong>: pretend every word was seen once more
          than it was.
        </P>
        <Exercise
          id="nb-classify"
          title="pWord, score, isSpam"
          {...EX.nbClassify}
          hints={[
            <>
              <C>{'(count + 1) / (total + model.vocab.size)'}</C>, where <C>count</C> comes from the right Map (or 0) and{' '}
              <C>total</C> is <C>spamWords</C> or <C>hamWords</C>.
            </>,
            <>
              Start <C>{'let s = Math.log(model.pSpam) - Math.log(1 - model.pSpam)'}</C>, then add each word’s log ratio.
            </>,
          ]}
        />
      </Step>

      <Step gate="nb-evaluate">
        <H>Part 3 · Test it honestly</H>
        <P>
          A model always looks better on the data it learned from. The real test is emails it has never seen. That’s why we
          train on the first 48 and keep 24 aside.
        </P>
        <Exercise
          id="nb-evaluate"
          title="accuracy and spammiest"
          {...EX.nbEvaluate}
          hints={[
            <>
              <C>{'set.filter(e => isSpam(model, e.text) === (e.spam === 1)).length / set.length'}</C>
            </>,
            <>
              Sort <C>[...model.vocab]</C> by the log ratio, biggest first, and take the first <C>k</C>.
            </>,
          ]}
        />
        <Callout kind="plain">
          Look at the mistake it prints. “Are you free for a quick call?” is a perfectly normal email, but “free” is such strong
          evidence for spam that it tips the balance. The model can’t tell “free gift” from “free for a call”, because it ignores
          word order. Fixing that takes models that read context — the subject of Modules 8 and 9.
        </Callout>
      </Step>

      <Step>
        <H>Module 5 complete</H>
        <P>
          That’s the mathematical foundation done. You can now read the equations behind modern AI: functions and graphs,
          vectors and matrices, derivatives and gradients, probability, likelihood and entropy. Next, we put them together into
          machines that learn.
        </P>
        <UL>
          <li>
            Stretch: add the two-word phrases (“click here”, “free for”) as extra “words”. Does the mistake go away?
          </li>
          <li>
            Stretch: instead of a hard yes/no, turn the score into a probability with <M>{'1 / (1 + e^{-\\text{score}})'}</M>.
            You’ll meet that function again very soon: it’s the sigmoid, at the heart of logistic regression.
          </li>
        </UL>
      </Step>
    </Lesson>
  )
}
